/**
 * Pair draft bills with captured inbox mail for review.
 * One mail is assigned to at most one bill. Amount (and extracted totals)
 * decide which bill a vendor invoice belongs to when several drafts share a sender.
 */

function normalizeEmail(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^<|>$/g, '');
}

function extractEmail(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const angle = raw.match(/<([^>]+@[^>]+)>/);
  if (angle) return normalizeEmail(angle[1]);
  if (raw.includes('@')) return normalizeEmail(raw.split(/\s+/).pop());
  return normalizeEmail(raw);
}

export function mailSenderEmail(email) {
  if (!email) return '';
  if (email.fromEmail) return extractEmail(email.fromEmail);
  if (typeof email.from === 'string') return extractEmail(email.from);
  if (email.from && typeof email.from === 'object') {
    return extractEmail(email.from.email || email.from.address || email.from.emailAddress?.address);
  }
  return '';
}

function configuredSenderList(options = {}) {
  const raw = options.configuredSenders || options.fromAddresses || [];
  const list = Array.isArray(raw) ? raw : String(raw || '').split(/[,;\n]+/);
  return list.map((item) => String(item || '').trim()).filter(Boolean);
}

function senderMatchesConfigured(sender, configuredSenders) {
  const s = extractEmail(sender);
  if (!s) return false;
  const senderDomain = s.split('@')[1] || '';
  return configuredSenderList({ configuredSenders }).some((item) => {
    const cfg = String(item || '').trim().toLowerCase().replace(/^@/, '');
    if (!cfg) return false;
    if (cfg.includes('@')) return s === extractEmail(cfg);
    return senderDomain === cfg || s.endsWith(`@${cfg}`);
  });
}

function vendorNameHitsText(vendorName, text) {
  const name = String(vendorName || '').trim().toLowerCase();
  const haystack = String(text || '').toLowerCase();
  if (!name || name.length < 3 || !haystack) return false;
  if (haystack.includes(name)) return true;
  const tokens = name.split(/\s+/).filter((t) => t.length >= 3 && t !== 'llc' && t !== 'inc' && t !== 'ltd');
  return tokens.some((t) => haystack.includes(t));
}

function vendorEmailForBill(bill, vendorEmailById = {}) {
  if (!bill) return '';
  if (bill.vendorEmail) return extractEmail(bill.vendorEmail);
  if (bill.email) return extractEmail(bill.email);
  const vendorId = bill.vendorId != null ? String(bill.vendorId) : '';
  return vendorId ? extractEmail(vendorEmailById[vendorId]) : '';
}

function parseAmount(value) {
  const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function amountsNearlyEqual(a, b, tolerance = 0.5) {
  if (a == null || b == null) return false;
  return Math.abs(a - b) <= tolerance;
}

function findAmountsInText(text) {
  const raw = String(text || '');
  const matches = raw.match(/\$?\s*\d{1,3}(?:,\d{3})*(?:\.\d{2})|\d+\.\d{2}/g) || [];
  return matches
    .map((m) => parseAmount(m))
    .filter((n) => n != null && n > 0);
}

function mailSearchText(email) {
  return [
    email?.subject,
    email?.bodyPreview,
    email?.snippet,
    email?.from,
    email?.fromName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function capturePairKey(billId, mailId) {
  return `${billId}::${mailId}`;
}

function amountHintsForMail(email, extractedAmount) {
  const found = findAmountsInText(mailSearchText(email));
  const extracted = parseAmount(extractedAmount);
  if (extracted != null && extracted > 0 && !found.some((n) => amountsNearlyEqual(n, extracted))) {
    found.push(extracted);
  }
  return found;
}

/**
 * @returns {{ score: number, reasons: string[], amountMatched: boolean, conflictingAmount: boolean }}
 */
export function scoreBillMailMatch(bill, email, options = {}) {
  const vendorEmailById = options.vendorEmailById || {};
  const otherBills = options.otherBills || [];
  const reasons = [];
  let score = 0;

  const billEmail = vendorEmailForBill(bill, vendorEmailById);
  const sender = mailSenderEmail(email);
  const billAmount = parseAmount(bill?.originalAmount ?? bill?.total ?? bill?.balance);
  const vendorName = String(bill?.vendorName || '').trim().toLowerCase();
  const haystack = mailSearchText(email);
  const fromName = String(email?.fromName || '').toLowerCase();
  const found = amountHintsForMail(email, options.extractedAmount);
  const configuredSenders = options.configuredSenders || [];

  const configuredHit = senderMatchesConfigured(sender, configuredSenders);
  if (billEmail && sender && billEmail === sender) {
    score += 60;
    reasons.push('Vendor email matches sender');
  } else if (configuredHit) {
    score += 40;
    reasons.push('Configured bill-mail sender');
  } else if (configuredSenders.length) {
    // Inbox mail is already filtered by Bill Mail Setup. Vendor contact email
    // (often the user's Gmail) is frequently not the invoice sender.
    score += 40;
    reasons.push('Captured bill mail');
  } else if (billEmail && sender) {
    const billDomain = billEmail.split('@')[1] || '';
    const senderDomain = sender.split('@')[1] || '';
    if (billDomain && billDomain === senderDomain) {
      score += 25;
      reasons.push('Same email domain');
    }
  }

  const amountMatched =
    billAmount != null && found.some((amt) => amountsNearlyEqual(amt, billAmount));

  let conflictingAmount = false;
  if (!amountMatched && found.length && otherBills.length) {
    conflictingAmount = otherBills.some((other) => {
      if (!other || String(other.id) === String(bill?.id)) return false;
      const otherAmt = parseAmount(other.originalAmount ?? other.total ?? other.balance);
      return otherAmt != null && found.some((amt) => amountsNearlyEqual(amt, otherAmt));
    });
  }

  if (amountMatched) {
    score += 40;
    reasons.push('Amount found in mail');
  }

  if (vendorNameHitsText(vendorName, haystack) || vendorNameHitsText(vendorName, fromName)) {
    score += 25;
    reasons.push('Vendor name in mail');
  }

  if (email?.hasAttachments) {
    score += 10;
    reasons.push('Has attachment');
  }

  return { score, reasons, amountMatched, conflictingAmount };
}

function toMatchRow(pair) {
  return {
    email: pair.email,
    score: pair.score,
    reasons: pair.reasons,
    amountMatched: !!pair.amountMatched,
  };
}

/**
 * Group draft bills with matching captured emails.
 * Each mail is used for at most one bill. Remaining related mails stay
 * available as alternates so the user can review and switch/clear.
 */
export function matchDraftBillsToMail(bills = [], emails = [], options = {}) {
  const minScore = options.minScore ?? 40;
  const vendorEmailById = options.vendorEmailById || {};
  const configuredSenders = options.configuredSenders || [];
  const dismissed = new Set(
    (options.dismissedPairs || []).map((key) => String(key))
  );
  const preferredMailByBillId = options.preferredMailByBillId || {};
  const extractedAmountByMailId = options.extractedAmountByMailId || {};

  const draftBills = (bills || []).filter(
    (b) => b && String(b.type || 'Bill') === 'Bill' && String(b.status || '').toLowerCase() === 'draft'
  );
  const mailList = (emails || []).filter((email) => email && email.id);

  const pairs = [];
  for (const bill of draftBills) {
    for (const email of mailList) {
      if (dismissed.has(capturePairKey(bill.id, email.id))) continue;
      const extractedAmount = extractedAmountByMailId[email.id] ?? extractedAmountByMailId[String(email.id)];
      const scored = scoreBillMailMatch(bill, email, {
        vendorEmailById,
        otherBills: draftBills,
        extractedAmount,
        configuredSenders,
      });
      if (scored.score < minScore) continue;
      pairs.push({
        bill,
        email,
        score: scored.score,
        reasons: scored.reasons,
        amountMatched: scored.amountMatched,
      });
    }
  }

  const usedBillIds = new Set();
  const usedMailIds = new Set();
  const assignment = [];

  const takePair = (pair) => {
    if (!pair?.bill || !pair?.email) return false;
    const bid = String(pair.bill.id);
    const mid = String(pair.email.id);
    if (usedBillIds.has(bid) || usedMailIds.has(mid)) return false;
    usedBillIds.add(bid);
    usedMailIds.add(mid);
    assignment.push(pair);
    return true;
  };

  draftBills.forEach((bill) => {
    const preferredId = preferredMailByBillId[String(bill.id)] ?? preferredMailByBillId[bill.id];
    if (preferredId == null || preferredId === '') return;
    const pair = pairs.find(
      (row) => String(row.bill.id) === String(bill.id) && String(row.email.id) === String(preferredId)
    );
    if (pair) takePair(pair);
  });

  const ranked = pairs.slice().sort((a, b) => {
    if (a.amountMatched !== b.amountMatched) return a.amountMatched ? -1 : 1;
    return b.score - a.score;
  });
  ranked.forEach((pair) => takePair(pair));

  // Keep a review group after "Not this mail" by assigning leftover inbox mail.
  draftBills.forEach((bill) => {
    if (usedBillIds.has(String(bill.id))) return;
    const leftover = mailList.find((email) => {
      if (!email?.id || usedMailIds.has(String(email.id))) return false;
      return !dismissed.has(capturePairKey(bill.id, email.id));
    });
    if (!leftover) return;
    takePair({
      bill,
      email: leftover,
      score: 0,
      reasons: ['Next captured mail'],
      amountMatched: false,
    });
  });

  const groups = assignment.map((pair) => {
    const bid = String(pair.bill.id);
    const primaryId = String(pair.email.id);
    const fromPairs = pairs
      .filter((row) => {
        if (String(row.bill.id) !== bid) return false;
        const mailId = String(row.email.id);
        if (mailId === primaryId) return false;
        return !usedMailIds.has(mailId);
      })
      .sort((a, b) => b.score - a.score);
    const seen = new Set(fromPairs.map((row) => String(row.email.id)));
    mailList.forEach((email) => {
      const mailId = String(email?.id || '');
      if (!mailId || mailId === primaryId || seen.has(mailId)) return;
      if (usedMailIds.has(mailId)) return;
      if (dismissed.has(capturePairKey(pair.bill.id, email.id))) return;
      fromPairs.push({
        bill: pair.bill,
        email,
        score: 0,
        reasons: ['Next captured mail'],
        amountMatched: false,
      });
      seen.add(mailId);
    });
    const alternates = fromPairs.slice(0, 8);

    const matches = [toMatchRow(pair), ...alternates.map(toMatchRow)];
    return {
      id: `capture-${pair.bill.id}`,
      bill: pair.bill,
      vendorEmail: vendorEmailForBill(pair.bill, vendorEmailById),
      matches,
      bestScore: pair.score,
      amountMatched: !!pair.amountMatched,
      confidence: pair.amountMatched || pair.score >= 80
        ? 'high'
        : pair.score >= 60
          ? 'medium'
          : 'possible',
    };
  });

  groups.sort((a, b) => b.bestScore - a.bestScore);

  const unmatchedMail = mailList.filter((email) => email?.id && !usedMailIds.has(String(email.id)));

  return {
    groups,
    unmatchedMail,
    matchedBillCount: groups.length,
    matchedMailCount: usedMailIds.size,
  };
}

export function formatMatchConfidence(confidence) {
  if (confidence === 'high') return 'Strong match';
  if (confidence === 'medium') return 'Likely match';
  return 'Possible match';
}
